'use client';

import { Fragment, useContext, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { faCircleCheck, faClock } from '@fortawesome/free-regular-svg-icons';
import { faExclamationTriangle, faPhone } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { LayoutContext } from '@/contexts/LayoutContext';
import { useEnv } from '@/env/client';
import { useFormApi } from '@lamesarv-sdk/hooks';
import { navigate, states } from '@lamesarv-sdk/tools';
import { useDynamicsForms } from '@/hooks/dynamicsForms';
import { useDevice } from '@/hooks/useDevice';
import { isFormDynamicsLocationEnabled } from '@/tools/forms';
import { parsePhone } from '@/tools/dynamics';
import { withSuspense } from '@/tools/withSuspense';

import { RichText } from '@lamesarv-sdk/components';
import { CommonForm } from '@/components/CommonForm';
import { CommonHidden } from '@/components/CommonForm/CommonHidden';
import { CommonInput } from '@/components/CommonForm/CommonInput';
import { CommonLocationSelect } from '@/components/CommonForm/CommonLocationSelect';
import { CommonMaskInput } from '@/components/CommonForm/CommonMaskInput';
import { CommonSelect } from '@/components/CommonForm/CommonSelect';

import { DynamicsLeadSource, DynamicsLeadType, IDynamicsFormMapping, IDynamicsOptionSet } from '@/types/dynamicsForms';
import { FormListTag, FormTag } from '@/types/forms';
import { IFormInput, IMetaData } from '@lamesarv-sdk/types';
import { IFormProps, IFormResetterProps } from '../types';

import { BaseButton, BaseOptInCheckbox, BaseSubmit } from '../base';
import { IFormSchema, validationSchema } from './schema';

import * as FormClasses from '../classes';

const formCaptchaErrorMessage = () => {
  return (
    <div className="w-full flex flex-col gap-2 p-4 mt-4 text-center justify-center text-red-600 bg-red-100 md:border border-red-300 rounded-lg">
      <div className="font-bold">Form Validation failed.</div>
      <div className="w-full">
        Please Call us at{' '}
        <a href="tel:833-289-6982" className="decoration-dashed underline underline-offset-4">
          <FontAwesomeIcon icon={faPhone} className="size-3.5 inline mr-1" />
          <span>833-289-6982</span>
        </a>{' '}
        to sell your RV
      </div>
      <div className="w-full">
        Or{' '}
        <a href="tel:866-687-2274" className="decoration-dashed underline underline-offset-4">
          <FontAwesomeIcon icon={faPhone} className="size-3.5 inline mr-1" />
          <span>866-687-2274</span>
        </a>{' '}
        to trade your RV for further assistance.
      </div>
    </div>
  );
};

enum FormPanel {
  rv = 'rv-information',
  personal = 'personal-information',
}

const formStyles = {
  container: 'leading-none grow relative',
  label:
    'absolute left-3 top-5 pointer-events-none leading-none text-black-400 text-sm md:text-md text-left [&>span]:text-md [&>span]:leading-none transition-all group-focus-within:left-2 group-focus-within:top-2 group-focus-within:-translate-y-0 group-focus-within:text-xs transition-all',
  labelWithValue: 'left-2 top-2 -translate-y-0 text-xs',
  input:
    'leading-none px-3 py-5 rounded-md focus:outline-none focus:ring-0 focus:border-black-500 focus:px-3 focus:pt-8 focus:pb-2',
  inputWithValue: 'px-3 pt-8 pb-2',
  button:
    'capitalize rounded-md bg-blue-500 text-white border-none hover:bg-blue-400 hover:text-white hover:border-none',
};

const FormResetter = (props: IFormResetterProps) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!props.wasSuccessful) return;

    reset();
    reset({ phone: '' });

    if (props.callback) {
      props.callback();
    }

    if (props.redirect) {
      navigate(props.redirect);
    }
  }, [props.wasSuccessful]);

  return null;
};

export interface IFormContactSellStepsProps extends IFormProps {
  locationsOptionSet?: IDynamicsOptionSet;
  metadata?: IMetaData;
  first_panel?: string;
  panel_rv_information_title?: string;
  panel_personal_information_title?: string;
  next_button_text?: string;
  message_success_title?: string;
  message_success_alternative?: string;
  restriction_minimum_year?: number;
  restriction_invalid_rv_types?: string;
  custom_years?: string;
  custom_rv_types?: string;
  success_button_text?: string;
  classes_next_button?: string;
  classes_success_title?: string;
  classes_success_text?: string;
  classes_success_button?: string;
}

export interface IFormPanelNavigationProps {
  stepNext: boolean;
  setStepNext: (value: boolean) => void;
  panelFirst: { title: string; fields: string[] };
  panelSecond: { title: string; fields: string[] };
}

const FormPanelNavigation = (props: IFormPanelNavigationProps) => {
  const { trigger } = useFormContext();
  const { stepNext, setStepNext } = props;

  return (
    <div className="flex flex-row justify-center gap-4">
      <div className="text-right">
        <div
          onClick={() => {
            setStepNext(false);
          }}
          className="flex flex-row items-center cursor-pointer text-blue-600 text-[14px] md:text-[16px] font-semibold"
        >
          <FontAwesomeIcon icon={faCircleCheck} className="w-8" />
          <span>{props.panelFirst.title}</span>
        </div>
      </div>
      <div className="text-left">
        <div
          onClick={async () => {
            const panelValidates = await trigger(props.panelFirst.fields);
            if (panelValidates) {
              setStepNext(true);
            }
          }}
          className={twMerge(
            'flex flex-row items-center cursor-pointer text-[14px] md:text-[16px] font-semibold',
            stepNext ? 'text-blue-600' : 'text-black-300 font',
          )}
        >
          <FontAwesomeIcon icon={faCircleCheck} className="w-8" />
          <span>{props.panelSecond.title}</span>
        </div>
      </div>
    </div>
  );
};

export interface IFormPanelProps extends IFormContactSellStepsProps {
  stepNext: boolean;
  setStepNext: (value: boolean) => void;
  panelFields: string[];
  isFirstPanel?: boolean;
  useFixedRVTypes?: {
    title: string;
    value: string;
  }[];
  useFixedYears?: {
    title: string;
    value: string;
  }[];
}

const FormPanelRV = (props: IFormPanelProps) => {
  const { trigger } = useFormContext();

  const isVisible = (!!props.isFirstPanel && !props.stepNext) || (!props.isFirstPanel && props.stepNext);

  return (
    <div className={twMerge('hidden flex-col', props.classes_content, isVisible && 'flex')}>
      <div className="flex flex-row flex-wrap -mx-2">
        <div className="my-2 px-2 w-full md:w-2/4">
          <CommonSelect
            name="contactType"
            label="Select Sell or Trade-In"
            options={[
              { title: 'Sell', value: 'sell' },
              { title: 'Trade-In', value: 'trade-in' },
            ]}
            isRequired
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
        <div className="my-2 px-2 w-full md:w-2/4">
          <CommonSelect
            name="rvType"
            label="RV Type"
            options={
              !!props.useFixedRVTypes?.length
                ? props.useFixedRVTypes
                : [
                    { title: 'Class A', value: 'Class A' },
                    { title: 'Class B', value: 'Class B' },
                    { title: 'Class C', value: 'Class C' },
                    { title: '5th Wheel', value: '5th Wheel' },
                    { title: 'Travel Trailers', value: 'Travel Trailers' },
                    { title: 'Other', value: 'Other' },
                  ]
            }
            isRequired
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
        <div className="my-2 px-2 w-full md:w-2/4">
          <CommonInput
            type="text"
            label="Make"
            name="make"
            isRequired
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
        <div className="my-2 px-2 w-full md:w-2/4">
          <CommonInput
            type="text"
            label="Model"
            name="model"
            isRequired
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
        <div className="my-2 px-2 w-full md:w-2/4">
          {!props.useFixedYears?.length && (
            <CommonInput
              type="number"
              label="Year"
              name="year"
              customOptions={{ min: 0, step: 1 }}
              isRequired
              classContainer={formStyles.container}
              classLabel={formStyles.label}
              classLabelWithValue={formStyles.labelWithValue}
              classInput={twMerge(
                '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
                formStyles.input,
                props.classes_input,
              )}
              classInputWithValue={formStyles.inputWithValue}
            />
          )}
          {!!props.useFixedYears?.length && (
            <CommonSelect
              name="year"
              label="Year"
              options={props.useFixedYears}
              isRequired
              classContainer={formStyles.container}
              classLabel={formStyles.label}
              classLabelWithValue={formStyles.labelWithValue}
              classInput={twMerge(formStyles.input, props.classes_input)}
              classInputWithValue={formStyles.inputWithValue}
            />
          )}
        </div>
        <div className="my-2 px-2 w-full md:w-2/4">
          <CommonInput
            type="number"
            label="Miles"
            name="miles"
            customOptions={{ min: 0, step: 1 }}
            isRequired
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(
              '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none',
              formStyles.input,
              props.classes_input,
            )}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
      </div>
      <div className="w-full">
        <div className="mt-2 text-[14px] md:text-[18px]">
          {!!props.isFirstPanel && (
            <BaseButton
              {...props}
              text={props.next_button_text || 'Next'}
              nameSlugForm="form-contact-sell"
              nameSlugButton="next"
              onClick={async () => {
                const panelValidates = await trigger(props.panelFields);
                if (panelValidates) {
                  props.setStepNext(true);
                }
              }}
              styleClasses={twMerge(
                FormClasses.buttonNormalClasses,
                formStyles.button,
                'w-full',
                props.classes_next_button,
              )}
            />
          )}
          {!props.isFirstPanel && (
            <BaseSubmit
              {...props}
              formNameSlug="form-contact-sell"
              styleClasses={twMerge(FormClasses.buttonNormalClasses, formStyles.button, 'w-full', props.classes_submit)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const FormPanelPersonal = (props: IFormPanelProps) => {
  const { trigger } = useFormContext();

  const isVisible = (!!props.isFirstPanel && !props.stepNext) || (!props.isFirstPanel && props.stepNext);

  return (
    <div className={twMerge('hidden flex-col', props.classes_content, isVisible && 'flex')}>
      <div className="flex flex-row flex-wrap -mx-2">
        <div className="my-2 px-2 w-full md:w-1/2">
          <CommonInput
            type="text"
            label="First Name"
            name="firstName"
            isRequired
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
        <div className="my-2 px-2 w-full md:w-1/2">
          <CommonInput
            type="text"
            label="Last Name"
            name="lastName"
            isRequired
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
        <div className="my-2 px-2 w-full md:w-1/2">
          <CommonInput
            type="email"
            label="Email"
            name="email"
            isRequired
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
        <div className="my-2 px-2 w-full md:w-1/2">
          <CommonMaskInput
            type="tel"
            mask="(999) 999-9999"
            label="Phone"
            name="phone"
            isRequired
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
        <div className="my-2 px-2 w-full md:w-1/2">
          <CommonLocationSelect
            label="Preferred Store"
            name="location"
            locationsOptionSet={props.locationsOptionSet}
            isRequired
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
        <div className="my-2 px-2 w-full md:w-1/2">
          <CommonInput
            type="text"
            label="Zip Code"
            name="zipCode"
            isRequired
            classContainer={formStyles.container}
            classLabel={formStyles.label}
            classLabelWithValue={formStyles.labelWithValue}
            classInput={twMerge(formStyles.input, props.classes_input)}
            classInputWithValue={formStyles.inputWithValue}
          />
        </div>
      </div>
      <div className="w-full">
        {props.display_sms_checkbox && (
          <div className="flex flex-row py-2 text-left">
            <BaseOptInCheckbox name="smsOptIn" nameDependency="phone" htmlContent={props.custom_sms_text} />
          </div>
        )}
        <div className="mt-2 text-[14px] md:text-[18px]">
          {!!props.isFirstPanel && (
            <BaseButton
              {...props}
              text={props.next_button_text || 'Next'}
              nameSlugForm="form-contact-sell"
              nameSlugButton="next"
              onClick={async () => {
                const panelValidates = await trigger(props.panelFields);
                if (panelValidates) {
                  props.setStepNext(true);
                }
              }}
              styleClasses={twMerge(
                FormClasses.buttonNormalClasses,
                formStyles.button,
                'w-full',
                props.classes_next_button,
              )}
            />
          )}
          {!props.isFirstPanel && (
            <BaseSubmit
              {...props}
              formNameSlug="form-contact-sell"
              styleClasses={twMerge(FormClasses.buttonNormalClasses, formStyles.button, 'w-full', props.classes_submit)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const FormContactSellSteps = withSuspense((props: IFormContactSellStepsProps) => {
  const [stepNext, setStepNext] = useState<boolean>(false);
  const [completed, setCompleted] = useState<boolean>(false);
  const [useAlternativeMessage, setUseAlternativeMessage] = useState<boolean>(false);

  const { context: layoutContext } = useContext(LayoutContext);
  const { fetchData: getDevice } = useDevice({ metadata: true, skipFetch: true });
  const { env } = useEnv();

  const [loadingLegacy, submittedLegacy, errorLegacy, sendFormLegacy] = useFormApi();
  const { loading, submitted, error, sendForm } = useDynamicsForms();

  const getLocationDetails = (location: string) => {
    const [city, stateCode] = location.split(',').map((item) => item.trim());
    const state = states.find((state) => state.postal === stateCode.toLowerCase())?.name || stateCode;

    return { city, state };
  };

  const getComments = (formInput: IFormSchema) => {
    const comments = formInput.comments ? formInput.comments.trim() : '';

    return [comments, '--', `Contact Type: ${formInput.contactType}`, `RV Type: ${formInput.rvType}`].join('\n');
  };

  const onSubmit = async (formInput: IFormSchema, metadata?: Record<string, unknown>) => {
    const { city, state } = getLocationDetails(formInput.location);

    const formMappings: IDynamicsFormMapping[] = [
      {
        DataverseFieldName: 'lmrv_leadtype',
        DataverseFieldValue: String(
          formInput.contactType === 'trade-in' ? DynamicsLeadType.seller : DynamicsLeadType.buyer,
        ),
      },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue: String(DynamicsLeadSource.website),
      },
      { FormFieldName: 'make', DataverseFieldName: 'lmrv_make' },
      { FormFieldName: 'year', DataverseFieldName: 'lmrv_vehicleyear' },
      { FormFieldName: 'model', DataverseFieldName: 'lmrv_model' },
      { FormFieldName: 'miles', DataverseFieldName: 'lmrv_miles' },
      { FormFieldName: 'firstName', DataverseFieldName: 'firstname' },
      { FormFieldName: 'email', DataverseFieldName: 'emailaddress1' },
      { FormFieldName: 'zipCode', DataverseFieldName: 'address1_postalcode' },
      { FormFieldName: 'lastName', DataverseFieldName: 'lastname' },
      { DataverseFieldName: 'address1_city', DataverseFieldValue: city },
      { DataverseFieldName: 'address1_stateorprovince', DataverseFieldValue: state },
      { DataverseFieldName: 'mobilephone', DataverseFieldValue: parsePhone(formInput.phone) },
      {
        DataverseFieldName: 'lmrv_dealership',
        DataverseFieldValue:
          String(props.locationsOptionSet.find((item) => item.Label === formInput.location)?.DataverseValue) || '',
      },
      {
        DataverseFieldName: 'lmrv_url',
        DataverseFieldValue: typeof location !== 'undefined' ? location?.href : '',
      },
      {
        DataverseFieldName: 'crbdb_segment',
        DataverseFieldValue: props.dynamics_segment,
      },
    ];

    // Check value restrictions to set alternative message if apply
    if (Number(formInput.year) < props.restriction_minimum_year) {
      setUseAlternativeMessage(true);
    }

    if (
      props.restriction_invalid_rv_types &&
      props.restriction_invalid_rv_types.toLowerCase().includes(formInput.rvType.toLowerCase())
    ) {
      setUseAlternativeMessage(true);
    }

    if (env.ENABLE_FORMS_CAPTURE_IP) {
      const device = await getDevice();
      formInput.ip = device.ip;
      formInput.user_agent = device.userAgent;
      formInput.platform = device.platform;
      formMappings.push(
        { DataverseFieldName: 'lmrv_ip', DataverseFieldValue: device.ip },
        { DataverseFieldName: 'lmrv_useragent', DataverseFieldValue: device.userAgent },
        { DataverseFieldName: 'lmrv_platform', DataverseFieldValue: device.platform },
      );
    }

    if (env.ENABLE_FORMS_CAPTURE_GEO && !!layoutContext.geoLocation) {
      formInput.geo_coordinates = layoutContext.geoLocation.join(',');
      formMappings.push({
        DataverseFieldName: 'lmrv_geocoordinates',
        DataverseFieldValue: layoutContext.geoLocation.join(','),
      });
    }

    if (env.ENABLE_FORMS_CAPTURE_CAPTCHA_SCORE && env.ENABLE_FORMS_CAPTCHA) {
      formInput.captcha_score = metadata?.captchaScore ? Number(metadata?.captchaScore) : undefined;
      formInput.captcha_threshold = metadata?.captchaThreshold ? Number(metadata?.captchaThreshold) : undefined;
      formMappings.push(
        {
          DataverseFieldName: 'lmrv_captchascore',
          DataverseFieldValue: formInput.captcha_score,
        },
        {
          DataverseFieldName: 'lmrv_captchathreshold',
          DataverseFieldValue: formInput.captcha_threshold,
        },
      );
    }

    if (env.ENABLE_FORMS_LEGACY) {
      const comments = getComments(formInput);
      sendFormLegacy({
        ...formInput,
        formType: 'custom',
        leadType: formInput.contactType === 'trade-in' ? 'seller' : 'buyer',
        leadSource: 'lamesarv.com',
        city,
        state,
        comments,
        extra_list_ids: [FormListTag.MASTER],
        extra_tag_ids: [
          formInput.contactType === 'trade-in' ? FormTag.CUSTOM_BUCKET_INTERNET : FormTag.CUSTOM_BUCKET_BUYER,
          FormTag.SELL_TRADE_MY_RV,
        ],
      } as IFormInput);
    }

    if (env.ENABLE_FORMS_DYNAMICS && isFormDynamicsLocationEnabled(env, formInput.location)) {
      await sendForm({
        formId: `#${props.custom_id || 'formContactSell'}`,
        mappings: formMappings,
        dynamicsFormId: env.DYNAMICS_FORM_ID_CONTACT_SELL,
      });
    }

    setCompleted(true);
  };

  const onSuccessClick = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  const _loading = loading || loadingLegacy || false;
  const _error = error || errorLegacy || false;
  const _submitted = submitted || submittedLegacy || false;

  const wasSuccessful = !_loading && !_error && _submitted;

  const panelFieldsRv = ['contactType', 'rvType', 'make', 'model', 'year', 'miles'];
  const panelFieldsPersonal = ['firstName', 'lastName', 'email', 'phone', 'location', 'zipCode'];

  const panelFirst = {
    title: props.first_panel === FormPanel.rv ? 'RV Information' : 'Personal Information',
    fields: props.first_panel === FormPanel.rv ? panelFieldsRv : panelFieldsPersonal,
  };

  const panelSecond = {
    title: props.first_panel === FormPanel.rv ? 'Personal Information' : 'RV Information',
    fields: props.first_panel === FormPanel.rv ? panelFieldsPersonal : panelFieldsRv,
  };

  // -- Prevent rendering if neither form system is enabled
  if (!env.ENABLE_FORMS_DYNAMICS && !env.ENABLE_FORMS_LEGACY) {
    return null;
  }

  return (
    <CommonForm
      id={props.custom_id || 'formContactSell'}
      schema={validationSchema}
      onSubmit={onSubmit}
      className={twMerge('font-helvetica relative', props.classes)}
      isDisabled={_loading || wasSuccessful}
      tagGroup={props.data_tag_group || 'form'}
      tagItem={props.data_tag ? `${props.data_tag}--form-contact-sell` : 'form-contact-sell'}
      customCaptchaErrorMessage={formCaptchaErrorMessage}
    >
      <CommonHidden name="formId" value="25" />
      <FormResetter wasSuccessful={wasSuccessful} callback={props.successCallback} redirect={props.redirect_url} />
      <div className={twMerge('w-full flex flex-col gap-4 p-2 text-center', props.classes_header)}>
        {props.title && (
          <div
            className={twMerge('text-[24px] md:text-[34px] font-bold leading-none text-blue-600', props.classes_title)}
          >
            {props.title}
          </div>
        )}
        {props.description && (
          <div className={twMerge('text-[14px] md:text-[16px] text-blue-600', props.classes_description)}>
            <RichText content={props.description} />
          </div>
        )}
      </div>
      {_loading && (
        <div
          className={
            'flex items-center gap-2 w-full bg-gray-100 border border-gray-500 text-gray-700 py-2 px-3 mb-4 rounded-md shadow-md'
          }
          role="alert"
        >
          <FontAwesomeIcon icon={faClock} className="w-8" />
          <span>Sending...</span>
        </div>
      )}
      {_submitted && _error && (
        <div
          className={'flex items-center w-full bg-red-100 border border-red-500 text-red-700 py-2 px-3 mb-4 rounded-md'}
          role="alert"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} className="w-8 mr-2" />
          <span>An error has occurred, please try again later or contact us using alternative methods.</span>
        </div>
      )}
      <div
        className={twMerge(
          'relative w-full flex flex-col gap-4 p-6 md:p-8 text-center justify-center bg-white md:border border-black-300 rounded-lg',
          props.classes_container,
        )}
      >
        <FormPanelNavigation
          panelFirst={panelFirst}
          panelSecond={panelSecond}
          stepNext={stepNext}
          setStepNext={setStepNext}
        />
        {props.first_panel === FormPanel.rv && (
          <Fragment>
            <FormPanelRV
              {...props}
              stepNext={stepNext}
              setStepNext={setStepNext}
              panelFields={panelFieldsRv}
              isFirstPanel
              useFixedYears={
                props.custom_years
                  ? props.custom_years
                      .split(',')
                      .filter(Boolean)
                      .map((year) => ({ title: year.trim(), value: year.trim() }))
                  : []
              }
              useFixedRVTypes={
                props.custom_rv_types
                  ? props.custom_rv_types
                      .split(',')
                      .filter(Boolean)
                      .map((type) => ({ title: type.trim(), value: type.trim() }))
                  : []
              }
            />
            <FormPanelPersonal
              {...props}
              stepNext={stepNext}
              setStepNext={setStepNext}
              panelFields={panelFieldsPersonal}
            />
          </Fragment>
        )}
        {props.first_panel === FormPanel.personal && (
          <Fragment>
            <FormPanelPersonal
              {...props}
              stepNext={stepNext}
              setStepNext={setStepNext}
              panelFields={panelFieldsPersonal}
              isFirstPanel
            />
            <FormPanelRV
              {...props}
              stepNext={stepNext}
              setStepNext={setStepNext}
              panelFields={panelFieldsRv}
              useFixedYears={
                props.custom_years
                  ? props.custom_years.split(',').map((year) => ({ title: year.trim(), value: year.trim() }))
                  : []
              }
              useFixedRVTypes={
                props.custom_rv_types
                  ? props.custom_rv_types.split(',').map((type) => ({ title: type.trim(), value: type.trim() }))
                  : []
              }
            />
          </Fragment>
        )}
        {/* Success Screen */}
        <div
          className={twMerge(
            'hidden absolute inset-1 bg-white flex-col items-center justify-center',
            props.classes_content,
            !!completed && 'flex',
          )}
        >
          <div
            className="w-full flex flex-col gap-4 px-10"
            data-tag-group="form-success-label"
            data-tag={
              props.data_tag ? `${props.data_tag}--form-contact-sell-success-label` : 'form-contact-sell-success-label'
            }
          >
            <div className={twMerge('px-14 text-[14px] md:text-[18px] font-bold', props.classes_success_title)}>
              {props.message_success_title}
            </div>
            <div className={twMerge('text-[14px] md:text-[16px]', props.classes_success_text)}>
              <RichText content={useAlternativeMessage ? props.message_success_alternative : props.message_success} />
            </div>
            <div className="px-14 text-[14px] md:text-[18px]">
              <BaseButton
                {...props}
                text={props.success_button_text || 'Thank You'}
                nameSlugForm="form-contact-sell"
                nameSlugButton="cta"
                onClick={onSuccessClick}
                styleClasses={twMerge(
                  FormClasses.buttonNormalClasses,
                  formStyles.button,
                  'w-full',
                  props.classes_success_button,
                )}
                forceDisabled={false}
              />
            </div>
          </div>
        </div>
      </div>
    </CommonForm>
  );
});
